import Box from '@mui/material/Box'
import CheckboxComponent from 'components/checkbox'
import InputMobileComponent from 'components/input-mobile'
import ModalConfirmComponent from 'components/modal-confirm'
import NavComponent from 'components/nav'
import SelectMobileComponent from 'components/select-mobile'
import { formatIncompletePhoneNumber, parsePhoneNumber } from 'libphonenumber-js/max'
import { useEffect, useState } from 'react'
import { UpsertStudentProps } from 'types/StudentTypes'
import { baseURL, defaultPhoneNumberCountry, phoneNumberRatesTooltip, phoneNumberTooltip } from 'utils/constants'
import { enabledRelationships } from 'utils/students'
import { isValidName, isValidPhone, isValidRelationship, isValidScheduleGroup } from 'utils/validations'
import './index.css'

interface UpsertStudentComponentProps {
  isShow: boolean
  isRegistered: boolean
  id: string
  name: string
  phone: string
  relationship: string
  student: string
  schedule: string
  scheduleGroupNames: string[]
  onActionCancel: () => void
  onActionSubmit: (data: UpsertStudentProps, sendSMS?: boolean) => void
}

const UpsertStudentComponent = (props: UpsertStudentComponentProps): JSX.Element => {
  const {
    isShow,
    isRegistered,
    id,
    name,
    relationship,
    phone,
    student,
    schedule,
    scheduleGroupNames,
    onActionCancel,
    onActionSubmit,
  } = props
  const [userName, setUserName] = useState<string>(name)
  const [userPhoneNumber, setUserPhoneNumber] = useState<string>(phone)
  const [userRelationship, setUserRelationship] = useState<string>(relationship)
  const [studentId, setStudentId] = useState<string>(id)
  const [studentName, setStudentName] = useState<string>(student)
  const [selectedScheduleGroup, setSelectedScheduleGroup] = useState<string>(schedule)
  const [sendSMS, setSendSMS] = useState(false)
  const [isConsent, setIsConsent] = useState(false)

  // Hooks
  useEffect(() => {
    if (isShow) return // Once modal is opened do not update internal state
    setUserName(name)
    setUserPhoneNumber(phone)
    setUserRelationship(relationship)
    setStudentId(id)
    setStudentName(student)
    setSelectedScheduleGroup(schedule || (scheduleGroupNames?.length === 1 ? scheduleGroupNames[0] : ''))
    setSendSMS(false)
    setIsConsent(false)
  }, [isShow, id, name, phone, relationship, student, schedule, scheduleGroupNames])

  // Handlers
  const formattedPhoneNumber: string = formatIncompletePhoneNumber(userPhoneNumber, defaultPhoneNumberCountry)
  const e164PhoneNumber: string = isValidPhone(userPhoneNumber, true)
    ? parsePhoneNumber(userPhoneNumber, defaultPhoneNumberCountry).format('E.164')
    : ''
  const isNew: boolean = id === ''
  const isValid: boolean =
    isValidScheduleGroup(scheduleGroupNames, selectedScheduleGroup, true) && // Ensures user created after school verification steps
    isValidName(studentName) &&
    isValidName(userName, true) &&
    isValidPhone(userPhoneNumber, true) &&
    isValidRelationship(userRelationship, true) &&
    (isConsent || !isNew)

  const handleAddUser = async () => {
    onActionSubmit(
      {
        userPhoneNumber: e164PhoneNumber,
        userName: userName.trim(),
        userRelationship,
        studentId,
        studentName: studentName.trim(),
        scheduleGroupName: selectedScheduleGroup,
      },
      sendSMS,
    )
  }

  const handleUpdateUser = async () => {
    onActionSubmit({
      userPhoneNumber: e164PhoneNumber,
      userName: userName,
      studentId,
      studentName: studentName.trim(),
      scheduleGroupName: selectedScheduleGroup,
    })
  }

  return (
    <ModalConfirmComponent
      title={isRegistered ? 'Edit Family' : 'Add Family'}
      actionIcon={'yellowAddIcon'}
      isShow={isShow}
      body={
        <Box display='flex' flexDirection='column' justifyContent='center' textAlign='left' gap={2} width='100%'>
          <InputMobileComponent
            type='text'
            labeltext='Adult Name'
            isRequired={true}
            isError={!isValidName(userName)}
            isReadOnly={isRegistered}
            value={userName}
            onChange={(v: string) => setUserName(v)}
          />
          <InputMobileComponent
            type='tel'
            labeltext='Phone Number'
            isRequired={true}
            isError={!isValidPhone(userPhoneNumber)}
            helperText={
              !isValidPhone(userPhoneNumber)
                ? phoneNumberTooltip()
                : isValidPhone(userPhoneNumber, true) && isNew
                  ? phoneNumberRatesTooltip()
                  : ''
            }
            isReadOnly={!isNew}
            value={isValidPhone(userPhoneNumber) ? formattedPhoneNumber : userPhoneNumber}
            onChange={(v: string) => setUserPhoneNumber(v)}
          />
          <SelectMobileComponent
            data_testid='relationship_testid'
            label='Relationship'
            isRequired={true}
            isError={!isValidRelationship(userRelationship)}
            isDisabled={isRegistered}
            isReadOnly={isRegistered}
            options={enabledRelationships}
            value={userRelationship}
            onChange={(v: string) => setUserRelationship(v)}
          />
          {scheduleGroupNames.length > 1 && (
            <SelectMobileComponent
              data_testid='schedule_testid'
              label='Select School Schedule'
              isRequired={true}
              isError={!isValidScheduleGroup(scheduleGroupNames, selectedScheduleGroup)}
              options={scheduleGroupNames} // Options from the fetched data
              value={selectedScheduleGroup}
              onChange={(v: string) => setSelectedScheduleGroup(v)}
            />
          )}
          <InputMobileComponent
            type='text'
            labeltext='Student Name'
            isRequired={true}
            isError={!isValidName(studentName)}
            value={studentName}
            onChange={(v: string) => setStudentName(v)}
          />
          {/* CheckboxComponent for sending SMS messages */}
          {isNew && (
            <Box display='flex' flexDirection='row' alignItems='center' sx={{ justifyContent: 'flex-start', mx: 2 }}>
              <CheckboxComponent
                isChecked={sendSMS}
                onClick={() => setSendSMS(!sendSMS)}
                label='Send account activation link via SMS to user'
              />
            </Box>
          )}
          {/* CheckboxComponent for consent */}
          {isNew && (
            <Box display='flex' flexDirection='row' alignItems='center' sx={{ justifyContent: 'flex-start', mx: 2 }}>
              <CheckboxComponent
                isChecked={isConsent}
                onClick={() => setIsConsent(!isConsent)}
                label={
                  <div>
                    I confirm that I have the authority to share this parent data with Carpool School Inc. This
                    information will be governed by the Carpool School Inc.{' '}
                    <span style={{ color: '#beac48', fontWeight: 'bold', textDecoration: 'underline' }}>
                      <NavComponent
                        href={`${baseURL}/home/index.html#privacy`}
                        text='Privacy Policy'
                        isOpenNewTab={true}
                      />
                    </span>
                    .
                  </div>
                }
              />
            </Box>
          )}
          {/* Consent text */}
          {!isNew && (
            <span
              className='text-gray-400 text-sm font-medium py-2'
              style={{ flex: 1, margin: 0, padding: 0, display: 'block', alignSelf: 'center', textAlign: 'left' }}
            >
              All data is governed by the Carpool School Inc.{' '}
              <span style={{ color: '#beac48', fontWeight: 'bold', textDecoration: 'underline' }}>
                <NavComponent href={`${baseURL}/home/index.html#privacy`} text='Privacy Policy' isOpenNewTab={true} />
              </span>
              .
            </span>
          )}
        </Box>
      }
      actionText={isNew ? 'Add' : 'Update'}
      onActionCancel={onActionCancel}
      onActionSubmit={isNew || !isRegistered ? handleAddUser : handleUpdateUser}
      isActionSubmitDisable={!isValid}
    />
  )
}

export default UpsertStudentComponent
